<template>
  <div class="public-aigame-container">
    <!-- 載入中 -->
    <div v-if="isLoading" class="loading-screen">
      <div class="loading-spinner"></div>
    </div>

    <!-- 錯誤 -->
    <div v-else-if="errorMsg" class="error-screen">
      <div class="error-icon">⚠️</div>
      <p>{{ errorMsg }}</p>
    </div>

    <!-- 遊戲畫面 -->
    <template v-else-if="gameData">
      <!-- 頂部資訊欄 -->
      <div class="game-topbar">
        <!-- Logo：點擊回到 Gallery -->
        <div class="arcade-logo" @click="router.push('/game-act-manager?view=explore')">
          <span class="arcade-logo-icon">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="white" xmlns="http://www.w3.org/2000/svg">
              <g transform="translate(-180,-4719)">
                <g transform="translate(56,160)">
                  <path d="M142,4570 C142,4569.448 141.552,4569 141,4569 L127,4569 C126.448,4569 126,4569.448 126,4570 L126,4576 C126,4576.552 126.448,4577 127,4577 L141,4577 C141.552,4577 142,4576.552 142,4576 L142,4570 Z M144,4569 L144,4577 C144,4578.105 143.105,4579 142,4579 L126,4579 C124.895,4579 124,4578.105 124,4577 L124,4569 C124,4567.895 124.895,4567 126,4567 L133,4567 L133,4563 C133,4561.895 133.895,4561 135,4561 L137,4561 C137.552,4561 138,4560.552 138,4560 L138,4559 L140,4559 L140,4561 C140,4562.105 139.105,4563 138,4563 L136,4563 C135.448,4563 135,4563.448 135,4564 L135,4567 L142,4567 C143.105,4567 144,4567.895 144,4569 L144,4569 Z M138,4574 L140,4574 L140,4572 L138,4572 L138,4574 Z M135,4572 C134.448,4572 134,4572.448 134,4573 C134,4573.552 134.448,4574 135,4574 C135.552,4574 136,4573.552 136,4573 C136,4572.448 135.552,4572 135,4572 L135,4572 Z M131,4572 L132,4572 L132,4574 L131,4574 L131,4575 L129,4575 L129,4574 L128,4574 L128,4572 L129,4572 L129,4571 L131,4571 L131,4572 Z"/>
                </g>
              </g>
            </svg>
          </span>
          <span class="arcade-logo-text">
            <span class="arcade-hiteach">HiTeach</span>
            <span class="arcade-name">ARCADE</span>
          </span>
        </div>
        <div class="game-title">{{ gameData.title }}</div>
        <div class="topbar-right">
          <!-- 開源徽章 -->
          <span class="opensource-badge" :class="gameData.isSourceCodeOpen ? 'open' : 'closed'">
            {{ gameData.isSourceCodeOpen ? t('gameActManager.openSource') : t('gameActManager.closedSource') }}
          </span>
          <span class="view-count" v-if="gameData.viewCount">
            <svg-icon icon-class="eye" class="eye-icon" />
            {{ gameData.viewCount }}
          </span>
          <!-- 複製到我的遊戲 -->
          <button class="copy-btn" @click="handleCopy" :disabled="isCopying">
            <span v-if="isCopying">{{ t('gameActManager.copying') }}</span>
            <span v-else>{{ t('gameActManager.copyToMyGames') }}</span>
          </button>
        </div>
      </div>

      <!-- iframe 遊戲本體 -->
      <iframe
        ref="gameIframe"
        :srcdoc="gameHtml"
        sandbox="allow-scripts"
        class="game-iframe"
      ></iframe>

    </template>
  </div>
</template>

<script setup>
/* eslint-disable */
import { ref, onMounted, inject } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import SvgIcon from '../components/SvgIcon.vue';
import gameActivityAPI from '@/api/gameActivityAPI';
import api from '@/api';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const $Message = inject('$Message');

const isLoading = ref(true);
const errorMsg = ref('');
const gameData = ref(null);
const gameHtml = ref('');
const gameIframe = ref(null);
const isCopying = ref(false);

const handleCopy = async () => {
  if (isCopying.value) return;

  // 從 localStorage 取得登入資訊（與 GameActManager 相同的登入機制）
  const profileStr = localStorage.getItem('profile');
  const accessToken = localStorage.getItem('access_token');
  if (!profileStr || !accessToken) {
    // 未登入：存 pendingAction，跳轉登入，登入後回到管理頁自動複製
    localStorage.setItem('pendingAction', JSON.stringify({
      action: 'copyPublicAIGame',
      publicId: gameData.value.id
    }));
    api.main.loginTmdIDWithRedirect(window.location.origin + '/game-act-manager?view=myGames');
    return;
  }

  let creatorId = '';
  let creatorName = '';
  try {
    const localeProfile = JSON.parse(profileStr);
    creatorId = localeProfile.id || localeProfile.teammodelId || '';
    creatorName = localeProfile.name || '';
  } catch (e) {
    $Message.error({ content: t('gameActManager.loginRequiredToCopy'), duration: 3 });
    return;
  }

  if (!creatorId) {
    $Message.error({ content: t('gameActManager.loginRequiredToCopy'), duration: 3 });
    return;
  }

  isCopying.value = true;
  try {
    const res = await gameActivityAPI.copyPublicGame({
      publicId: gameData.value.id,
      creator: { id: creatorId, name: creatorName }
    });
    if (res?.status === 200) {
      $Message.success({ content: t('gameActManager.copySuccess'), duration: 2 });
      setTimeout(() => {
        router.push('/game-act-manager?view=myGames');
      }, 1500);
    } else {
      $Message.error({ content: t('gameActManager.copyGameFailed'), duration: 3 });
    }
  } catch (e) {
    console.error('[PublicAIGamePlay] 複製失敗:', e);
    $Message.error({ content: t('gameActManager.copyGameFailed'), duration: 3 });
  } finally {
    isCopying.value = false;
  }
};

const loadPublicGame = async (publicId) => {
  try {
    const res = await gameActivityAPI.getPublicGame(publicId);
    if (res?.status !== 200 || !res?.data) {
      errorMsg.value = '找不到指定的公開遊戲';
      return;
    }
    const data = res.data;
    gameData.value = data;

    // 從 gameContentSettings 取 customCode
    if (data.gameContentSettings) {
      const settings = JSON.parse(data.gameContentSettings);
      gameHtml.value = settings.customCode || '';
    }

    if (!gameHtml.value) {
      errorMsg.value = '遊戲內容為空';
    }
  } catch (e) {
    console.error('[PublicAIGamePlay] 載入失敗:', e);
    errorMsg.value = '載入遊戲失敗，請稍後再試';
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  const publicId = route.query.publicId;
  if (!publicId) {
    errorMsg.value = '缺少 publicId 參數';
    isLoading.value = false;
    return;
  }
  loadPublicGame(publicId);
});
</script>

<style lang="less" scoped>
.public-aigame-container {
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #1a1a2e;
  overflow: hidden;
}

.loading-screen,
.error-screen {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #fff;
  gap: 16px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255,255,255,0.2);
  border-top-color: #667eea;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-icon { font-size: 48px; }

.game-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  background: rgba(0,0,0,0.4);
  color: #fff;
  flex-shrink: 0;
  height: 44px;
  gap: 12px;

  .arcade-logo {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    flex-shrink: 0;
    opacity: 0.9;
    transition: opacity 0.15s;

    &:hover { opacity: 1; }

    .arcade-logo-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .arcade-logo-text {
      white-space: nowrap;
      display: inline-flex;
      align-items: baseline;
      gap: 3px;

      .arcade-hiteach {
        font-weight: 800;
        font-size: 13px;
        color: #fff;
      }

      .arcade-name {
        font-family: Impact, 'Arial Black', sans-serif;
        font-size: 15px;
        letter-spacing: 1px;
        color: #fff;
      }
    }
  }

  .game-title {
    font-size: 16px;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
  }

  .topbar-right {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
  }

  .opensource-badge {
    font-size: 11px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 10px;
    letter-spacing: 0.3px;

    &.open {
      background: rgba(52, 211, 153, 0.2);
      color: #34d399;
      border: 1px solid rgba(52, 211, 153, 0.4);
    }

    &.closed {
      background: rgba(75, 85, 99, 0.6);
      color: #fff;
      border: 1px solid rgba(255, 255, 255, 0.2);
      font-weight: 700;
    }
  }

  .view-count {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 13px;
    opacity: 0.8;

    .eye-icon {
      width: 16px;
      height: 16px;
    }
  }

  .copy-btn {
    padding: 5px 14px;
    background: #667eea;
    color: #fff;
    border: none;
    border-radius: 6px;
    font-size: 13px;
    cursor: pointer;
    transition: background 0.2s;
    white-space: nowrap;

    &:hover:not(:disabled) {
      background: #5a6fd6;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}

.game-iframe {
  flex: 1;
  width: 100%;
  border: none;
  display: block;
}

</style>
